import React, { useEffect, useState } from 'react';
import { ordersAPI } from '@/utils/api';
import { Link } from 'react-router-dom';
import { Package, ChevronRight, Calendar, CreditCard } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge'; // Assuming you have a Badge component, or standard div if not

export const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getMyOrders();
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper for Status Colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-muted/20" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-outfit font-semibold text-3xl mb-8">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-border shadow-sm">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
            <p className="text-muted-foreground mb-6">Looks like you haven't bought anything yet.</p>
            <Link to="/products" className="text-primary font-medium hover:underline">
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/order-confirmation/${order.id}`}
                className="block bg-white rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow duration-200"
              >
                {/* --- Card Header --- */}
                <div className="bg-muted/30 p-4 sm:p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-lg">
                        #{order.wordpress_order_id || order.id.slice(0, 8)}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(order.status)} capitalize`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : 'N/A'}
                      </div>
                      <div className="flex items-center gap-1">
                        <CreditCard className="w-3.5 h-3.5" />
                        Total: ₹{order.total?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center text-primary text-sm font-medium">
                    View Details <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>

                {/* --- Product List (Detailed) --- */}
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    {order.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex gap-4 items-start">
                        {/* Image */}
                        <div className="w-16 h-20 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden border border-border">
                          <img
                            src={item.product_image || "https://placehold.co/100?text=No+Img"}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm sm:text-base text-foreground line-clamp-1">
                            {item.product_name || "Product Name Unavailable"}
                          </h4>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                            <span>Qty: {item.quantity}</span>
                            {item.size && (
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/40" /> 
                                Size: {item.size}
                              </span>
                            )}
                            {item.color && (
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/40" /> 
                                Color: {item.color}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Price (Optional on summary card) */}
                        <div className="text-sm font-medium">
                          ₹{(item.product_price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer if more items exist */}
                  {order.items.length > 3 && (
                    <div className="mt-4 pt-3 border-t border-dashed border-border text-center">
                      <span className="text-sm text-muted-foreground">
                        + {order.items.length - 3} more items in this order
                      </span>
                    </div>
                  )}
                  
                  {/* Mobile View Details Link (Visible only on small screens) */}
                  <div className="sm:hidden mt-4 pt-4 border-t border-border flex items-center justify-center text-primary text-sm font-medium">
                    View Full Order Details <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};